import { choiceQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { PositionBadge, positionColours } from "./PositionBadge";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { TitledTabs } from "components/common/titledTabs";
import { useTenantTranslation } from "hooks/formatters";
import type {
  SensorTopology,
  ScrapEyeRecordingPosition,
  ScrapEyeSensorConnectionRead,
  ScrapEyeSensorPlacementRead,
} from "src/store/api/generatedApi";
import {
  activeConnections,
  activeInstallations,
  TopologyEdit,
  TopologySelection,
} from "./topologyEditing";
import { SENSOR_TYPE_LABEL_KEYS } from "./sensorTypeLabels";
import { formatInstallationTimestamp } from "./installationTimestamp";

export type TopologyCorrection =
  | { kind: "connection"; connection: ScrapEyeSensorConnectionRead }
  | { kind: "installation"; installation: ScrapEyeSensorPlacementRead };

interface SensorTopologyDetailsProps {
  topology: SensorTopology;
  positions: ScrapEyeRecordingPosition[];
  selection: TopologySelection | null;
  busy: boolean;
  onEdit: (edit: TopologyEdit) => void;
  onCorrect: (correction: TopologyCorrection) => void;
  onClear: () => void;
}

export const SensorTopologyDetails = ({
  topology,
  positions,
  selection,
  busy,
  onEdit,
  onCorrect,
  onClear,
}: SensorTopologyDetailsProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const colours = positionColours(positions, theme);
  const [tab, setTab] = useNavigationQuery(
    "equipmentView",
    "relationships",
    choiceQuery(["relationships", "history"] as const),
    "push"
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const connections = activeConnections(topology);
  const installations = activeInstallations(topology);
  const sensorIds = new Set(
    selection == null
      ? []
      : selection.kind === "sensor"
        ? [selection.id]
        : selection.kind === "device"
          ? connections
              .filter((connection) => connection.device_id === selection.id)
              .map((connection) => connection.sensor_id)
          : installations
              .filter(
                (installation) =>
                  installation.recording_position_id === selection.id
              )
              .map((installation) => installation.sensor_id)
  );
  const selectedConnections = connections.filter((connection) =>
    selection?.kind === "device"
      ? connection.device_id === selection.id
      : sensorIds.has(connection.sensor_id)
  );
  const selectedInstallations = installations.filter((installation) =>
    sensorIds.has(installation.sensor_id)
  );
  const selectedSensor =
    selection?.kind === "sensor"
      ? topology.sensors.find((sensor) => sensor.id === selection.id)
      : null;
  const selectedName =
    selection?.kind === "sensor"
      ? selectedSensor?.name
      : selection?.kind === "device"
        ? topology.devices.find((device) => device.id === selection.id)?.name
        : positions.find((position) => position.id === selection?.id)?.name;
  const connectionHistory = topology.connections.filter((connection) =>
    selection?.kind === "device"
      ? connection.device_id === selection.id
      : sensorIds.has(connection.sensor_id)
  );
  const installationHistory = topology.installations.filter((installation) =>
    selection?.kind === "position"
      ? installation.recording_position_id === selection.id
      : sensorIds.has(installation.sensor_id)
  );
  return (
    <Stack
      spacing={1.5}
      sx={{
        border: 1,
        borderColor: "divider",
        p: 2,
        height: 494,
        overflow: "auto",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" sx={{ flex: 1, overflowWrap: "anywhere" }}>
          {selectedName ?? t("topologySelectEquipment")}
        </Typography>
        {selection != null && (
          <>
            {selection.kind !== "position" && (
              <IconButton
                aria-label={t("actions")}
                onClick={(event) => {
                  setMenuAnchor(event.currentTarget);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            )}
            <IconButton
              aria-label={t("topologyClearSelection")}
              onClick={onClear}
            >
              <CloseIcon />
            </IconButton>
          </>
        )}
      </Stack>
      {selectedSensor != null && (
        <Typography variant="body2" color="text.secondary">
          {selectedSensor.serial_number ??
            t(SENSOR_TYPE_LABEL_KEYS[selectedSensor.sensor_type])}
        </Typography>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={menuAnchor != null}
        onClose={() => {
          setMenuAnchor(null);
        }}
      >
        <MenuItem
          disabled={
            busy ||
            selectedConnections.length +
              (selection?.kind === "sensor"
                ? selectedInstallations.length
                : 0) ===
              0
          }
          onClick={() => {
            setMenuAnchor(null);
            onEdit({
              kind: "end",
              connections: selectedConnections,
              installations:
                selection?.kind === "sensor" ? selectedInstallations : [],
            });
          }}
        >
          {t("topologyEndRelationships")}
        </MenuItem>
      </Menu>
      {selection != null && (
        <TitledTabs
          selectedTabKey={tab}
          onChange={setTab}
          tabSpecs={[
            {
              key: "relationships",
              title: t("topologyRelationships"),
              content: (
                <Stack spacing={2} sx={{ pt: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="subtitle2">
                      {t("sensorConnectionsTitle")}
                    </Typography>
                    {selection.kind === "sensor" && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          onEdit({
                            kind: "connect",
                            sensorId: selection.id,
                            deviceId: null,
                          });
                        }}
                      >
                        {t("add")}
                      </Button>
                    )}
                  </Stack>
                  {selectedConnections.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {t("topologyNoConnections")}
                    </Typography>
                  )}
                  {selectedConnections.map((connection) => (
                    <Stack
                      key={connection.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2">
                          {selection.kind === "sensor"
                            ? connection.device_name
                            : connection.sensor_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {connection.device_name} /{" "}
                          {connection.device_sensor_id ?? t("topologyLink")}
                        </Typography>
                      </Stack>
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          onEdit({
                            kind: "reconnect",
                            connection,
                            deviceId: connection.device_id,
                          });
                        }}
                      >
                        {t("edit")}
                      </Button>
                    </Stack>
                  ))}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="subtitle2">
                      {t("sensorInstallationsTitle")}
                    </Typography>
                    {selection.kind === "sensor" && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          onEdit({
                            kind: "install",
                            sensorId: selection.id,
                            positionId: null,
                          });
                        }}
                      >
                        {t("add")}
                      </Button>
                    )}
                  </Stack>
                  {selectedInstallations.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {t("topologyNoInstallations")}
                    </Typography>
                  )}
                  {selectedInstallations.map((installation) => (
                    <Stack
                      key={installation.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <PositionBadge
                          colour={
                            colours.get(installation.recording_position_id) ??
                            theme.palette.text.secondary
                          }
                          name={installation.recording_position_name}
                          location={installation.location_name}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {installation.location_name}
                          {selection.kind !== "sensor" &&
                            ` / ${installation.sensor_name}`}
                        </Typography>
                      </Stack>
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          onEdit({
                            kind: "move",
                            installation,
                            positionId: installation.recording_position_id,
                          });
                        }}
                      >
                        {t("edit")}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              ),
            },
            {
              key: "history",
              title: t("history"),
              content: (
                <Stack spacing={2} sx={{ pt: 2 }}>
                  {connectionHistory.map((connection) => (
                    <Stack key={connection.id} spacing={0.5}>
                      <Typography variant="body2">
                        {connection.device_name}
                      </Typography>
                      <Typography variant="caption">
                        {formatInstallationTimestamp(connection.connected_at)} —{" "}
                        {connection.disconnected_at == null
                          ? t("topologyOpenEnded")
                          : formatInstallationTimestamp(
                              connection.disconnected_at
                            )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("topologyRecordedAt", {
                          time: formatInstallationTimestamp(
                            connection.recorded_at
                          ),
                        })}
                      </Typography>
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          onCorrect({ kind: "connection", connection });
                        }}
                      >
                        {t("topologyCorrectRecord")}
                      </Button>
                    </Stack>
                  ))}
                  {installationHistory.map((installation) => (
                    <Stack key={installation.id} spacing={0.5}>
                      <Typography variant="body2">
                        {installation.location_name} /{" "}
                        {installation.recording_position_name}
                      </Typography>
                      <Typography variant="caption">
                        {formatInstallationTimestamp(installation.installed_at)}{" "}
                        —{" "}
                        {installation.removed_at == null
                          ? t("topologyOpenEnded")
                          : formatInstallationTimestamp(
                              installation.removed_at
                            )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("topologyRecordedAt", {
                          time: formatInstallationTimestamp(
                            installation.recorded_at
                          ),
                        })}
                      </Typography>
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          onCorrect({ kind: "installation", installation });
                        }}
                      >
                        {t("topologyCorrectRecord")}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              ),
            },
          ]}
        />
      )}
    </Stack>
  );
};
